import { X, Car, Users, Edit2, Clock, MapPin, History, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import type { POI, Accomplice } from '../store/demoStore';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { cn, isValidImageUrl } from '../lib/utils';

interface Props {
  poi: POI | null;
  onClose: () => void;
  onEdit: (poi: POI) => void;
  onDelete?: (poiId: string) => Promise<void> | void;
  readOnly?: boolean;
}

export function POIDetailModal({ poi, onClose, onEdit, onDelete, readOnly }: Props) {
  const { t } = useTranslation();
  const [nestedPoi, setNestedPoi] = useState<POI | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  if (!poi) return null;

  const handleAccompliceClick = (acc: Accomplice) => {
    setNestedPoi({
      id: acc.id,
      code: acc.code,
      name: acc.name,
      threatLevel: acc.threatLevel,
      photoUrl: acc.photoUrl,
      notes: acc.notes || '',
      lastSeen: poi.lastSeen,
      accomplices: undefined,
      vehicle: undefined,
    });
  };

  const getThreatLevelText = (level: string) => {
    return t(`threat.${(level || 'low').toLowerCase()}`);
  };

  return (
    <>
      {/* Main Modal */}
      {createPortal(
      <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="bg-white dark:bg-gray-900 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
          {/* Header */}
          <div className="sticky top-0 bg-inherit p-4 border-b flex justify-between items-center">
            <h2 className="text-xl font-bold font-mono">{poi.code}</h2>
            <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-800">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 space-y-4">
            {isValidImageUrl(poi.photoUrl) ? (
              <img src={poi.photoUrl} alt={poi.code} className="w-32 h-32 rounded-full mx-auto object-contain bg-muted" />
            ) : (
              <div className="w-32 h-32 rounded-full mx-auto bg-muted flex items-center justify-center text-xs text-muted-foreground">
                {t('admin.noImagesAvailable')}
              </div>
            )}
            <div className="text-center">
              <span className={`inline-block px-3 py-1 rounded-full text-sm font-bold mt-1 ${
                poi.threatLevel === 'High' ? 'bg-red-100 text-red-800' :
                poi.threatLevel === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                'bg-blue-100 text-blue-800'
              }`}>
                {getThreatLevelText(poi.threatLevel)} {t('guard.threat')}
              </span>
              <div className="text-center mt-2">
              <Clock className="w-4 h-4 inline mr-1 text-muted-foreground" />
              {poi.lastSeen
                ? new Date(poi.lastSeen).toLocaleString()
                : t('common.unknown')}
            </div>
            </div>

            <div>
              <h3 className="font-semibold flex items-center gap-2">
              <MapPin className="w-4 h-4" /> {t('guard.lastSeen')}</h3>
              {poi.lastSeen && (
                    <p className="text-ms text-muted-foreground mb-2">
                    <Clock className="w-4 h-4 inline mr-1 text-muted-foreground" />
                      {new Date(poi.lastSeen).toLocaleString()}
                    </p>
                  )}
            </div>

            <div>
              <h3 className="font-semibold">{t('guard.notes')}</h3>
              <p className="text-muted-foreground">{poi.notes}</p>
            </div>

            {poi.accomplices && poi.accomplices.length > 0 && (
              <div>
                <h3 className="font-semibold flex items-center gap-2"><Users className="w-4 h-4" /> {t('guard.accomplices')}</h3>
                <div className="space-y-2 mt-1">
                  {poi.accomplices.map(acc => (
                    <div
                      key={acc.id}
                      className="border rounded p-2 text-sm flex gap-2 items-center cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                      onClick={() => handleAccompliceClick(acc)}
                    >
                      {isValidImageUrl(acc.photoUrl) ? (
                        <img src={acc.photoUrl} className="w-10 h-10 rounded-full object-contain bg-muted" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-xs text-muted-foreground">
                          {t('admin.noImagesAvailable')}
                        </div>
                      )}
                      <div>
                        <p className="font-mono text-xs font-medium">{acc.code}</p>
                        <span className={cn(
                            "inline-block px-2 py-0.5 rounded-full text-xs",
                            acc.threatLevel === 'High' && "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
                            acc.threatLevel === 'Medium' && "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
                            acc.threatLevel === 'Low' && "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
                          )}>
                            {getThreatLevelText(acc.threatLevel)}
                        </span>
                          {poi.lastSeen && (
                        <p className="text-ms text-muted-foreground mt-1">
                          {t('guard.lastSeen')}: {new Date(poi.lastSeen).toLocaleString()}
                        </p>
                        )}
                        {acc.notes && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{acc.notes}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {poi.vehicle && (
              <div>
                <h3 className="font-semibold flex items-center gap-2"><Car className="w-4 h-4" /> {t('guard.getawayVehicle')}</h3>
                <p>{t('guard.licensePlate')}: {poi.vehicle.licensePlate}</p>
                <p>{poi.vehicle.make} {poi.vehicle.model} – {poi.vehicle.color}</p>
                {poi.vehicle.location && <p>{t('guard.location')}: {poi.vehicle.location}</p>}
                {poi.vehicle.photoUrl ? (
                  isValidImageUrl(poi.vehicle.photoUrl) ? (
                    <img src={poi.vehicle.photoUrl} className="mt-2 rounded max-h-32 object-contain bg-muted" />
                  ) : (
                    <div className="w-full h-32 rounded bg-muted flex items-center justify-center text-xs text-muted-foreground">
                      {t('admin.noImagesAvailable')}
                    </div>
                  )
                ) : null}
                {/* Driver display */}
                {(poi.vehicle as any).driverPhotoUrl && (poi.vehicle as any).driverPhotoUrl.startsWith('http') && (
                  <div className="mt-3 pt-2 border-t">
                    <p className="text-sm font-semibold mb-1">{t('guard.accomplice.driver')}:</p>
                    <div className="flex items-center gap-2">
                      <img
                        src={(poi.vehicle as any).driverPhotoUrl}
                        className="w-10 h-10 rounded-full object-cover bg-muted"
                        alt="Driver"
                        onError={(e) => (e.currentTarget.style.display = 'none')}
                      />
                      <span className="text-sm font-mono">{(poi.vehicle as any).driverCode}</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {poi.editHistory && poi.editHistory.length > 0 && (
              <details className="mt-4 border-t pt-4">
                <summary className="cursor-pointer font-semibold flex items-center gap-2 text-sm">
                  <History className="w-4 h-4" /> {t('admin.editHistory')} ({poi.editHistory.length})
                </summary>
                <div className="mt-3 space-y-2 max-h-60 overflow-y-auto text-sm">
                  {poi.editHistory.map((entry, idx) => (
                    <div key={idx} className="border-l-2 border-muted pl-3 py-1">
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>{entry.editedByName || entry.editedBy}</span>
                        <span>{new Date(entry.editedAt).toLocaleString()}</span>
                      </div>
                      <div className="mt-1">
                        <span className="font-medium">{entry.field}:</span>{' '}
                          <div>
                          <span className="line-through text-red-600">
                            {typeof entry.oldValue === 'object'
                            ? JSON.stringify(entry.oldValue).substring(0, 40) + '…'
                            : String(entry.oldValue)}
                          </span>
                          {' → '}
                          </div>
                          <span className="text-green-600">
                            {typeof entry.newValue === 'object'
                            ? JSON.stringify(entry.newValue).substring(0, 40) + '…'
                            : String(entry.newValue)}
                          </span>
                      </div>
                    </div>
                  ))}
                </div>
              </details>
            )}

            {!readOnly && (
              <div className="pt-4 flex justify-end gap-2">
                {onDelete && (
                  <Button
                    variant="destructive"
                    disabled={isDeleting}
                    onClick={async () => {
                      if (!confirm(t('admin.confirmDeletePOI') || 'Delete this POI permanently?')) return;
                      setIsDeleting(true);
                      try {
                        await onDelete(poi.id);
                        onClose(); // only close after deletion succeeds
                      } catch (err) {
                        console.error('Delete failed:', err);
                        alert('Failed to delete POI. Please try again.');
                      } finally {
                        setIsDeleting(false);
                      }
                    }}
                  >
                    <Trash2 className="w-4 h-4 mr-1" /> {isDeleting ? 'Deleting...' : t('common.delete')}
                  </Button>
                )}
                <Button onClick={() => onEdit(poi)} variant="outline" className="gap-2">
                  <Edit2 className="w-4 h-4" /> {t('admin.editPOI')}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>,
      document.body
      )}

      {/* Nested Modal for clicked accomplice */}
      {nestedPoi && (
        <POIDetailModal
          poi={nestedPoi}
          onClose={() => setNestedPoi(null)}
          onEdit={onEdit}
        />
      )}
    </>
  );
}